/**
 * Staff menu layout (services/nav_layout.py): a staff member's own
 * arrangement of the Hub's left-hand menu.
 *
 * The layout never adds pages: it's applied on top of the standard,
 * permission-filtered menu. A page in the layout they can no longer open
 * drops out; a page they can open that the layout doesn't mention (new
 * since they saved) goes into its usual group if they kept that group,
 * else just after the page it normally follows (a whole new group arrives
 * as a group).
 */
import { api } from "@/lib/api";

export type NavItem = { to: string; icon: any; label: string };
export type NavGroup = { label: string; icon: any; children: NavItem[]; id?: string; defaultId?: string | null };
export type NavEntry = NavItem | NavGroup;

export type LayoutEntry =
  | { t: "i"; to: string }
  | { t: "g"; id: string; label: string; defaultId: string | null; children: string[] };
export type NavLayout = { entries: LayoutEntry[]; hidden: string[] };

export function isGroup(entry: NavEntry): entry is NavGroup {
  return "children" in entry;
}

export const defaultGroupId = (label: string) => `d:${label}`;
export const newGroupId = () => `c:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Every page in the standard menu, in order, with the group it sits in. */
export function flatten(defaults: NavEntry[]): Array<{ item: NavItem; group: NavGroup | null }> {
  const out: Array<{ item: NavItem; group: NavGroup | null }> = [];
  for (const e of defaults) {
    if (isGroup(e)) e.children.forEach((c) => out.push({ item: c, group: e }));
    else out.push({ item: e, group: null });
  }
  return out;
}

/** The standard menu as a layout (what the editor starts from). */
export function layoutOf(entries: NavEntry[]): NavLayout {
  return {
    entries: entries.map((e): LayoutEntry => isGroup(e)
      ? { t: "g", id: e.id ?? defaultGroupId(e.label), label: e.label,
          defaultId: e.defaultId !== undefined ? e.defaultId : defaultGroupId(e.label),
          children: e.children.map((c) => c.to) }
      : { t: "i", to: e.to }),
    hidden: [],
  };
}

export function applyLayout(defaults: NavEntry[], layout: NavLayout | null | undefined, folderIcon: any): NavEntry[] {
  if (!layout) return defaults;
  const flat = flatten(defaults);
  const byPath = new Map(flat.map((f) => [f.item.to, f]));
  const defaultGroups = new Map(defaults.filter(isGroup).map((g) => [defaultGroupId(g.label), g]));
  const hidden = new Set(layout.hidden);
  const placed = new Set<string>();

  const out: NavEntry[] = [];
  const groups: NavGroup[] = [];
  for (const e of layout.entries) {
    if (e.t === "i") {
      const f = byPath.get(e.to);
      if (f && !hidden.has(e.to) && !placed.has(e.to)) { out.push(f.item); placed.add(e.to); }
    } else {
      const kids: NavItem[] = [];
      for (const to of e.children) {
        const f = byPath.get(to);
        if (f && !hidden.has(to) && !placed.has(to)) { kids.push(f.item); placed.add(to); }
      }
      const g: NavGroup = { id: e.id, label: e.label, defaultId: e.defaultId,
        icon: (e.defaultId && defaultGroups.get(e.defaultId)?.icon) || folderIcon, children: kids };
      groups.push(g);
      out.push(g);
    }
  }

  // Pages the layout doesn't mention: new since it was saved.
  // A new group arrives as that group, just after what it normally follows.
  flat.forEach(({ item, group }, i) => {
    if (placed.has(item.to) || hidden.has(item.to)) return;
    placed.add(item.to);
    const home = group && groups.find((g) => g.defaultId === defaultGroupId(group.label));
    if (home) { home.children.push(item); return; }
    let at = out.length;
    for (let j = i - 1; j >= 0; j--) {
      const prev = flat[j].item.to;
      const k = out.findIndex((e) => isGroup(e) ? e.children.some((c) => c.to === prev) : e.to === prev);
      if (k >= 0) { at = k + 1; break; }
    }
    if (group) {
      const g: NavGroup = { id: defaultGroupId(group.label), label: group.label, defaultId: defaultGroupId(group.label),
        icon: group.icon, children: [item] };
      groups.push(g);
      out.splice(at, 0, g);
    } else {
      out.splice(at, 0, item);
    }
  });

  return out.filter((e) => !isGroup(e) || e.children.length > 0);
}

type Resp = { layout: NavLayout | null };
export const navLayoutApi = {
  get: () => api.getNavLayout() as Promise<Resp>,
  save: (layout: NavLayout) => api.saveNavLayout(layout) as Promise<Resp>,
  reset: () => api.resetNavLayout() as Promise<Resp>,
};
